import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Phone, Quote } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumbSchema";
import { CASE_STUDIES } from "@/lib/caseStudiesData";

const SITE_URL = "https://coloradowebimpressions.com";
const PAGE_URL = `${SITE_URL}/case-studies`;
const ORG_ID = `${SITE_URL}/#organization`;

const COLLECTION_LD = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": PAGE_URL,
  url: PAGE_URL,
  name: "Real Results for Colorado Businesses — Case Studies",
  description:
    "Real case studies from Colorado businesses across HVAC, plumbing, roofing, landscaping, electrical, and cleaning verticals.",
  publisher: { "@id": ORG_ID },
  hasPart: CASE_STUDIES.map((c) => ({
    "@type": "Article",
    "@id": `${SITE_URL}/case-studies/${c.slug}#article`,
    headline: c.headline,
    url: `${SITE_URL}/case-studies/${c.slug}`,
    datePublished: c.datePublished,
    dateModified: c.datePublished,
    author: { "@id": `${SITE_URL}/#person` },
    image: `${SITE_URL}/logo.png`,
  })),
};

const STATS = [
  { value: "200+", label: "Websites Built" },
  { value: "$8M+", label: "Revenue Generated" },
  { value: "97%", label: "Client Retention" },
  { value: "150+", label: "5-Star Reviews" },
];

export const Route = createFileRoute("/case-studies")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
        { name: "description", content: "Real Colorado SEO and web design case studies. See ranking gains, lead growth, and revenue results from HVAC, roofing, legal, and home-service clients." },
      {
        title: "Colorado Case Studies — Real Local Business Results",
      },
      {
        name: "description",
        content:
          "Real case studies from Colorado businesses — HVAC, plumbing, roofing, landscaping, electrical, and cleaning. Owner-led marketing from Colorado Springs.",
      },
      {
        property: "og:title",
        content: "Real Results for Colorado Businesses",
      },
      {
        property: "og:description",
        content:
          "From Colorado Springs to Denver to Pueblo — actual outcomes we've delivered for Colorado companies.",
      },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Real Results for Colorado Businesses" },
      {
        name: "twitter:description",
        content:
          "Real case studies from Colorado businesses — HVAC, plumbing, roofing, landscaping, electrical, and cleaning. Owner-led marketing from Colorado Springs.",
      },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(COLLECTION_LD),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildBreadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Case Studies" },
          ]),
        ),
      },
    ],
  }),
  component: CaseStudiesPage,
});

function ServicePill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-brand-gold/10 px-2.5 py-1 text-[11px] font-bold text-brand-gold-deep">
      {children}
    </span>
  );
}

function CaseStudyCard({ c }: { c: (typeof CASE_STUDIES)[number] }) {
  return (
    <article className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:-translate-y-0.5 hover:border-brand-gold/60 hover:shadow-card-hover sm:p-7">
      {/* Industry + city */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-foreground/5 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-foreground">
          {c.industry}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-semibold text-foreground/80">
          <MapPin className="h-3 w-3 text-brand-gold-deep" />
          {c.location}
        </span>
      </div>

      {/* Challenge */}
      <p className="mt-4 text-sm italic leading-relaxed text-muted-foreground">
        {c.challenge}
      </p>

      {/* Services */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {c.service.map((s) => (
          <ServicePill key={s}>{s}</ServicePill>
        ))}
      </div>

      {/* Hero result */}
      <div className="mt-6 rounded-xl border border-brand-gold/30 bg-brand-gold/5 p-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-brand-gold-deep">
          Result
        </div>
        <div className="mt-1 text-2xl font-black leading-tight tracking-tight text-foreground">
          {c.heroResult}
        </div>
      </div>

      {/* Quote */}
      <figure className="mt-5 flex-1">
        <Quote className="h-5 w-5 text-brand-gold-deep" />
        <blockquote className="mt-2 text-sm italic leading-relaxed text-foreground/85">
          &ldquo;{c.cardQuote}&rdquo;
        </blockquote>
      </figure>

      {/* CTA */}
      <Link
        to="/case-studies/$studySlug"
        params={{ studySlug: c.slug }}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-4 py-2.5 text-sm font-bold text-brand-black shadow-gold transition-spring group-hover:-translate-y-0.5"
      >
        Read full case study
        <ArrowRight className="h-4 w-4" />
      </Link>
    </article>
  );
}

function CaseStudiesPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Marketing Case Studies"
        title={
          <>
            Real Results for{" "}
            <span className="text-brand-gold">Colorado Businesses</span>
          </>
        }
        description="We don't sell impressions or vanity metrics. Here's what we've actually delivered for Colorado companies — from Colorado Springs to Denver to Pueblo."
      >
        <div className="flex justify-center">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Case Studies" }]} />
        </div>
      </PageHero>

      {/* Stats bar */}
      <section className="border-b border-border bg-section-alt">
        <div className="mx-auto max-w-6xl px-5 py-8 lg:px-8">
          <ul className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
            {STATS.map((s) => (
              <li
                key={s.label}
                className="text-center"
              >
                <div className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">
                  {s.value}
                </div>
                <div className="mt-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {s.label}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Case study grid */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:gap-8">
            {CASE_STUDIES.map((c) => (
              <CaseStudyCard key={c.slug} c={c} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Want similar results for your Colorado business?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Owner-led, no middlemen. Talk directly with Chris about what would
            actually move the needle for your business.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a Free Proposal
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
